import { Provider } from '../classes/angular-entities';

type IBrowserStorage = Storage;

export interface IValueStorage {
    length: number;
    set(key: string, value: any): void;
    get(key: string): any;
    remove(key: string): void;
    key(n: number): any | null;
}

export class ValueStorageProvider extends Provider {
    private _storageKeyPrefix: string;
    private _storageType: string;

    public static get $name() {
        return 'valueStorage';
    }

    public static get $getInject() {
        return ['$log'];
    }

    constructor() {
        super();
        this._storageKeyPrefix = '';
        this._storageType = 'sessionStorage';
    }

    public setKeyPrefix(prefix: string): void {
        if (typeof prefix !== 'string') {
            throw new TypeError('[valueStorage] - ValueStorageProvider.setKeyPrefix() expects a String.');
        }
        this._storageKeyPrefix = prefix;
    }

    public setStorageType(storageType: string): void {
        // dummy
        this._storageType = storageType;
    }

    public $get($log: any) {
        let storage = this.checkStorageSupported();
        const isSupported = !!storage;
        if (!isSupported) {
            $log.warn('This browser does not support Web Storage!');
            storage = new FallbackStorage();
        }
        return new ValueStorage(storage, this._storageKeyPrefix);
    }

    private checkStorageSupported(): IBrowserStorage {
        let supported = null;
        try {
            supported = (window as any)[this._storageType];
        } catch (err) {
            supported = null;
        }

        if (supported) {
            const key = '__' + Math.round(Math.random() * 1e7);
            try {
                supported.setItem(key, key);
                supported.removeItem(key, key);
            } catch (err) {
                supported = null;
            }
        }

        return supported;
    }
}

type TFallbackStorageItem = {
    key: string;
    value: any;
};

class FallbackStorage implements IBrowserStorage {
    private data: TFallbackStorageItem[] = [];

    public setItem(key: string, value: any): void {
        const index = this.keyIndex(key);
        if (index !== -1) {
            this.data[index].value = value;
        } else {
            this.data.push({ key: key, value: value });
        }
    }

    public getItem(key: string): any {
        const index = this.keyIndex(key);
        if (index !== -1) {
            return this.data[index].value;
        }
        return null;
    }

    public removeItem(key: string): void {
        const index = this.keyIndex(key);
        if (index !== -1) {
            this.data.splice(index, 1);
        }
    }

    public get length(): number {
        return this.data.length;
    }

    public key(n: number): string | null {
        if (this.data[n]) {
            return this.data[n].key;
        } else {
            return null;
        }
    }

    private keyIndex(key: string): number {
        const index = this.data.findIndex((item: TFallbackStorageItem) => item.key === key);
        return index;
    }

    public clear() {
        this.data = [];
    }
}

class ValueStorage implements IValueStorage {
    private storag: IBrowserStorage;
    private prefix: string;

    constructor(storag: IBrowserStorage, prefix: string) {
        this.storag = storag;
        this.prefix = prefix;
    }

    public set(key: string, value: any): void {
        this.storag.setItem(this.prefix + key, JSON.stringify(value));
    }

    public get(key: string): any {
        const value: any = this.storag.getItem(this.prefix + key);
        let result: any;
        if (value) {
            try {
                result = JSON.parse(value);
            } catch (error) {
                result = value;
            }
        } else {
            result = value;
        }
        return result;
    }

    public remove(key: string): void {
        this.storag.removeItem(this.prefix + key);
    }

    public get length(): number {
        return this.storag.length;
    }

    public key(n: number): string | null {
        return this.storag.key(n);
    }
}
